/**
 * Encodes/decodes the "FDA Sources view, linked/unlinked-only" deep-link
 * state (source + link status + optional FDA Sourcing Reach filter
 * selections) to/from flat URL query params
 * (?fdaSource=<source>&linkFilter=<unlinked|linked>&obType=...&
 * ndcFinished=...&dmfStatus=...), separate from searchFilterCodec.ts's
 * base64-encoded AdvancedSearchQuery `?filter=` param. See
 * docs/features/fda-source-record-browser.md Architecture Decision 4 --
 * "source + link status" is a handful of flat enum-ish values, not a
 * SearchRule, so a base64 blob adds nothing here.
 *
 * `linkFilter=linked` and the filter params were added for the "Sourceable
 * via Navigator" deep link (human direction, 2026-09-28) -- reusing the
 * exact same `getEligibleObSql`/`getEligibleNdcSql`/`getEligibleDmfSql`
 * filter combinations already backing the FDA Sourcing Reach dashboard
 * cards, so the browse list's total always equals the card's own count for
 * whichever combination is currently selected (see
 * api/routes/fdaSourceSearch.js's `obType`/`ndcFinished`/`dmfStatus` query
 * params). "Not Yet Sourceable" (`linkFilter=unlinked`) now threads the
 * same filter selections through too, for the same consistency reason --
 * its own count already depends on them (DashboardMetrics.tsx's
 * `obReach`/`ndcReach`/`usdmfReach` selectors).
 *
 * Backward compatible: a bookmarked/shared legacy URL
 * (`?fdaSource=ob&unlinked=true`, no `linkFilter` param) still decodes as
 * `linkFilter: 'unlinked'`, no filters -- the pre-existing contract.
 */

import type { FdaSourceKey } from '../services/api/domains/products/types';
import type {
  ObStatus,
  NdcFinished,
  UsdmfStatus,
} from '../components/metrics/fdaSourcingReachSelectors';
import {
  isOverviewCohortKey,
  type OverviewCohortKey,
} from '../components/metrics/overviewCohorts';

const FDA_SOURCE_KEYS: FdaSourceKey[] = ['ob', 'ndc', 'dmf'];
// 'cohort' (docs/features/metrics-overview-tab.md, Requirement 5): a Metrics
// Overview card's ingredient cohort, Orange Book only, with a `cohort` key.
const LINK_FILTER_VALUES = ['unlinked', 'linked', 'cohort'] as const;

export type FdaSourceLinkFilter = (typeof LINK_FILTER_VALUES)[number];

export interface FdaSourceLinkParams {
  source: FdaSourceKey;
  linkFilter: FdaSourceLinkFilter;
  /** Only present/relevant when linkFilter === 'cohort' (source === 'ob'). */
  cohort?: OverviewCohortKey;
  /** Only present/relevant when source === 'ob'. */
  obType?: ObStatus[];
  /** Only present/relevant when source === 'ndc'. */
  ndcFinished?: NdcFinished[];
  /** Only present/relevant when source === 'dmf'. */
  dmfStatus?: UsdmfStatus[];
}

function isFdaSourceKey(value: string | null): value is FdaSourceKey {
  return value !== null && (FDA_SOURCE_KEYS as string[]).includes(value);
}

/** Only an empty selection collapses to undefined -- 'both selected' returns
 * the real 'both' combo (Copilot review, PR #836: this comment previously
 * said "both selected -> undefined", which is incorrect and matters because
 * omission invokes the server's own default rule, not a true "both"). */
export function ndcFinishedToCombo(values: NdcFinished[]): string | undefined {
  const hasFinished = values.includes('finished');
  const hasUnfinished = values.includes('unfinished');
  if (hasFinished && hasUnfinished) return 'both';
  if (hasFinished) return 'finishedOnly';
  if (hasUnfinished) return 'unfinishedOnly';
  return undefined;
}

export function usdmfStatusToCombo(values: UsdmfStatus[]): string | undefined {
  const hasActive = values.includes('active');
  const hasInactive = values.includes('inactive');
  if (hasActive && hasInactive) return 'both';
  if (hasActive) return 'activeOnly';
  if (hasInactive) return 'inactiveOnly';
  return undefined;
}

function comboToNdcFinished(combo: string | null): NdcFinished[] | undefined {
  if (combo === 'finishedOnly') return ['finished'];
  if (combo === 'unfinishedOnly') return ['unfinished'];
  if (combo === 'both') return ['finished', 'unfinished'];
  return undefined;
}

function comboToUsdmfStatus(combo: string | null): UsdmfStatus[] | undefined {
  if (combo === 'activeOnly') return ['active'];
  if (combo === 'inactiveOnly') return ['inactive'];
  if (combo === 'both') return ['active', 'inactive'];
  return undefined;
}

/** Encodes source + link status + (source-relevant) filter selection into
 * a flat query string. `filters` is ignored entirely for a source it
 * doesn't apply to (e.g. `ndcFinished` when `source !== 'ndc'`). */
export function encodeFdaSourceUnlinkedParams(
  source: FdaSourceKey,
  linkFilter: FdaSourceLinkFilter = 'unlinked',
  filters?: {
    obType?: ObStatus[];
    ndcFinished?: NdcFinished[];
    dmfStatus?: UsdmfStatus[];
    cohort?: OverviewCohortKey;
  }
): string {
  const params = new URLSearchParams();
  params.set('fdaSource', source);
  params.set('linkFilter', linkFilter);

  if (linkFilter === 'cohort') {
    // A cohort link carries only its key -- the status filters don't apply.
    if (filters?.cohort) params.set('cohort', filters.cohort);
    return params.toString();
  }

  if (source === 'ob' && filters?.obType && filters.obType.length > 0) {
    params.set('obType', filters.obType.join(','));
  }
  if (source === 'ndc' && filters?.ndcFinished) {
    const combo = ndcFinishedToCombo(filters.ndcFinished);
    if (combo) params.set('ndcFinished', combo);
  }
  if (source === 'dmf' && filters?.dmfStatus) {
    const combo = usdmfStatusToCombo(filters.dmfStatus);
    if (combo) params.set('dmfStatus', combo);
  }

  return params.toString();
}

/**
 * Decodes `fdaSource`/`linkFilter`/filter params from a URLSearchParams
 * instance. Returns null when `fdaSource` is missing/unrecognized or no
 * valid link-status signal is present -- fails closed, never throws,
 * mirroring searchFilterCodec.ts's decodeFilter contract.
 */
export function decodeFdaSourceUnlinkedParams(
  searchParams: URLSearchParams
): FdaSourceLinkParams | null {
  const source = searchParams.get('fdaSource');
  if (!isFdaSourceKey(source)) {
    return null;
  }

  const rawLinkFilter = searchParams.get('linkFilter');
  // Legacy contract (pre-2026-09-28): no linkFilter param, just
  // unlinked=true -- still decodes as linkFilter: 'unlinked'.
  const linkFilter: FdaSourceLinkFilter | undefined =
    rawLinkFilter &&
    (LINK_FILTER_VALUES as readonly string[]).includes(rawLinkFilter)
      ? (rawLinkFilter as FdaSourceLinkFilter)
      : searchParams.get('unlinked') === 'true'
        ? 'unlinked'
        : undefined;
  if (!linkFilter) {
    return null;
  }

  const result: FdaSourceLinkParams = { source, linkFilter };
  if (linkFilter === 'cohort') {
    // Fails closed: a cohort link is Orange Book only and needs a known key.
    const cohort = searchParams.get('cohort');
    if (source !== 'ob' || !isOverviewCohortKey(cohort)) {
      return null;
    }
    result.cohort = cohort;
    return result;
  }
  if (source === 'ob') {
    const rawObType = searchParams.get('obType');
    if (rawObType) {
      result.obType = rawObType.split(',').filter(Boolean) as ObStatus[];
    }
  }
  if (source === 'ndc') {
    const ndcFinished = comboToNdcFinished(searchParams.get('ndcFinished'));
    if (ndcFinished) result.ndcFinished = ndcFinished;
  }
  if (source === 'dmf') {
    const dmfStatus = comboToUsdmfStatus(searchParams.get('dmfStatus'));
    if (dmfStatus) result.dmfStatus = dmfStatus;
  }
  return result;
}
